/**
 * File: assets/breed-modal.core.js
 * Core logic:
 * - storage
 * - normalization
 * - breeds loading + size detection
 * - fetching products from filtered collection HTML
 * - resolving correct variant + price for chosen size
 */
(() => {
  const NS = '[BreedModal/Core]';
  const log = (...args) => console.log(NS, ...args);

  // -------------------------
  // Storage (safe)
  // -------------------------
  function safeGet(key) {
    try {
      return localStorage.getItem(key) || '';
    } catch {
      return '';
    }
  }

  function safeSet(key, val) {
    try {
      localStorage.setItem(key, val);
    } catch {}
  }

  function safeRemove(key) {
    try {
      localStorage.removeItem(key);
    } catch {}
  }

  // -------------------------
  // Normalization
  // -------------------------
  const norm = (s) => String(s || '').trim();

  function normSearch(s) {
    return norm(s)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\u00a0/g, ' ')
      .replace(/[–—]/g, '-')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function uniqueSorted(list) {
    const set = new Set();

    (list || []).forEach((x) => {
      const v = norm(x);
      if (v) set.add(v);
    });

    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  // -------------------------
  // Breeds helpers
  // -------------------------
  function safeJsonParse(s) {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }

  function flattenGroupedBreeds(parsed) {
    const buckets = ['small', 'medium', 'large'];
    const out = [];

    buckets.forEach((k) => {
      const arr = parsed?.[k];
      if (Array.isArray(arr)) out.push(...arr);
    });

    return out;
  }

  function buildBreedSizeMap(groupedJson) {
    const map = Object.create(null);
    const buckets = ['small', 'medium', 'large'];

    for (const k of buckets) {
      const arr = groupedJson?.[k];
      if (!Array.isArray(arr)) continue;

      for (const breed of arr) {
        const key = normSearch(breed);
        if (key && map[key] == null) map[key] = k;
      }
    }

    return map;
  }

  // -------------------------
  // Collection parsing helpers
  // -------------------------
  function pickBestSrcFromSrcset(srcset) {
    const parts = String(srcset || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return '';

    const last = parts[parts.length - 1];
    const url = last.split(' ')[0].trim();

    return url || '';
  }

  function normalizeImgUrl(u) {
    let img = String(u || '').trim();

    if (!img) return '';
    if (img.startsWith('//')) img = `https:${img}`;
    if (img.startsWith('/')) img = `${location.origin}${img}`;

    return img;
  }

  function cleanPriceText(t) {
    const s = String(t || '')
      .replace(/\s+/g, ' ')
      .replace(/Regular price|Sale price|From|Unit price/gi, '')
      .trim();

    /* Breed results (owner, 2026-10-29): any currency sign (\p{Sc}), not only the seven once
       listed. Without the peso sign no pattern matched and the whole price row was kept. */
    const m = s.match(/(\p{Sc}|AUD|USD|EUR|GBP)\s?[\d.,]+/iu);
    if (m && m[0]) return m[0].trim();

    const m2 = s.match(/[\d.,]+\s?\p{Sc}/u);
    if (m2 && m2[0]) return m2[0].trim();

    if (/\d/.test(s)) return s;

    return '';
  }

  /* Breed results (owner, 2026-10-29): an element's text without its `hidden` parts. The bed
     finder's card keeps a "Sold out" flag in its price row, hidden while the bed is in stock, and
     textContent reads hidden text too. */
  function visibleText(el) {
    if (!el) return '';
    const copy = typeof el.cloneNode === 'function' ? el.cloneNode(true) : null;
    if (!copy || typeof copy.querySelectorAll !== 'function') return el.textContent || '';
    copy.querySelectorAll('[hidden]').forEach((node) => node.remove());
    return copy.textContent || '';
  }

  function getHandleFromUrl(productUrl) {
    try {
      const u = new URL(productUrl, location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');

      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];

      return '';
    } catch {
      const m = String(productUrl || '').match(/\/products\/([^/?#]+)/);
      return m?.[1] || '';
    }
  }

  function formatMoneyFromCents(cents, currency) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';

    const curr = currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || '';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: curr || 'USD',
        currencyDisplay: 'symbol',
      }).format(n / 100);
    } catch {
      return `${(n / 100).toFixed(2)}${curr ? ` ${curr}` : ''}`;
    }
  }

  // -------------------------
  // Public API (core)
  // -------------------------
  function createCore(config) {
    const {
      shownKey,
      selectedBreedKey,
      breedsUrl,
      breedsUrlFallback,
      fallbackBreeds,
      bedSizeUrls,
      treeSizeUrls,
      scratcherSizeUrls,
      sizeOptionValueMap,
      collectionGridSectionId,
      variantOptionNameHints,
      maxVariantFetch = 30,
    } = config;

    let breedsGrouped = null;
    let breedSizeMap = null;

    const productJsonCache = new Map();
    const productJsonInflight = new Map();

    const collectionProductsCache = new Map();
    const collectionProductsInflight = new Map();

    function alreadyShown() {
      return safeGet(shownKey) === '1';
    }

    function markShown() {
      safeSet(shownKey, '1');
    }

    function getSelectedBreed() {
      return safeGet(selectedBreedKey);
    }

    function setSelectedBreed(breed) {
      safeSet(selectedBreedKey, breed);
    }

    function removeSelectedBreed() {
      safeRemove(selectedBreedKey);
    }

    async function loadBreeds() {
      const tryFetch = async (url) => {
        const res = await fetch(url, { cache: 'force-cache' });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const text = await res.text();
        const parsed = safeJsonParse(text);

        if (Array.isArray(parsed?.breeds)) {
          breedsGrouped = null;
          breedSizeMap = null;
          return { list: parsed.breeds, grouped: null };
        }

        const flat = flattenGroupedBreeds(parsed);

        if (flat.length) {
          breedsGrouped = parsed;
          breedSizeMap = buildBreedSizeMap(parsed);
          return { list: flat, grouped: parsed };
        }

        throw new Error('Invalid JSON format');
      };

      try {
        return await tryFetch(breedsUrl);
      } catch (e1) {
        try {
          return await tryFetch(breedsUrlFallback);
        } catch (e2) {
          log('Failed to load breeds list, using fallback list', e1, e2);
          breedsGrouped = null;
          breedSizeMap = null;
          return { list: fallbackBreeds || [], grouped: null };
        }
      }
    }

    function detectSizeForBreed(breed) {
      const key = normSearch(breed);

      if (!key) return '';

      if (breedSizeMap && breedSizeMap[key]) {
        return breedSizeMap[key];
      }

      if (!breedsGrouped) return '';

      const buckets = ['small', 'medium', 'large'];

      for (const k of buckets) {
        const arr = breedsGrouped?.[k];

        if (!Array.isArray(arr)) continue;

        if (arr.some((x) => normSearch(x) === key)) {
          return k;
        }
      }

      return '';
    }

    function getSizeOptionValue(sizeKey) {
      return sizeOptionValueMap?.[sizeKey] || '';
    }

    function urlWithSections(url, sectionId) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('sections', sectionId);
      return u.toString();
    }

    async function fetchCollectionSectionHTML(collectionUrl) {
      const requestUrl = urlWithSections(collectionUrl, collectionGridSectionId);

      log('Fetching collection section:', requestUrl);

      const res = await fetch(requestUrl, { credentials: 'same-origin' });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const keys = Object.keys(json || {});

      log('Sections JSON keys:', keys);

      const hasKey = Object.prototype.hasOwnProperty.call(json || {}, collectionGridSectionId);

      let html = hasKey ? json[collectionGridSectionId] : '';

      if (!hasKey && keys.length) {
        html = json[keys[0]] || '';
        log('SectionId key not found. Using first returned key:', keys[0]);
      }

      const len = typeof html === 'string' ? html.length : 0;

      log('Section HTML length:', len);

      return typeof html === 'string' ? html : '';
    }

    function findProductGridRoot(doc) {
      return (
        /* Breed results (owner, 2026-10-29): the redesigned collection grid's results, which the
           size in the URL filters. Without it the whole <main> was read, the bed finder's cards
           too, which ignore the size: a large cat was shown 16 beds, 4 of them its size. */
        doc.querySelector('[data-pz-grid-swap="results"]') ||
        doc.querySelector('#ProductGridContainer') ||
        doc.querySelector('[id*="ProductGridContainer"]') ||
        doc.querySelector('.product-grid-container') ||
        doc.querySelector('.product-grid') ||
        doc.querySelector('.grid.product-grid') ||
        doc.querySelector('main') ||
        doc.body
      );
    }

    async function fetchCollectionProductsFallback(collectionUrl, limit = 30) {
      const cacheKey = `${collectionUrl}::${limit}`;

      if (collectionProductsCache.has(cacheKey)) {
        return collectionProductsCache.get(cacheKey);
      }

      if (collectionProductsInflight.has(cacheKey)) {
        return collectionProductsInflight.get(cacheKey);
      }

      const request = (async () => {
        log('Fallback: fetching full collection HTML:', collectionUrl);

        const requestUrl = new URL(collectionUrl, window.location.href).toString();

        const res = await fetch(requestUrl, {
          credentials: 'include',
          cache: 'no-store',
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const root = findProductGridRoot(doc);

        let anchors = Array.from(root.querySelectorAll('a[href*="/products/"]'));

        if (!anchors.length) {
          anchors = Array.from(doc.querySelectorAll('a[href*="/products/"]'));
        }

        const products = [];
        const seen = new Set();

        for (const a of anchors) {
          const hrefRaw = a.getAttribute('href') || '';

          if (!hrefRaw.includes('/products/')) continue;

          const cleanHref = hrefRaw.split('?')[0];
          const url = cleanHref.startsWith('http') ? cleanHref : `${location.origin}${cleanHref}`;

          if (seen.has(url)) continue;

          const card =
            a.closest('[data-product-id]') ||
            a.closest('.card-wrapper') ||
            a.closest('.card') ||
            a.closest('.grid__item') ||
            a.closest('li') ||
            a.parentElement;

          if (!card) continue;

          const hasPriceHint =
            !!card.querySelector('.price') ||
            !!card.querySelector('.price-item') ||
            !!card.querySelector('[class*="price"]');

          const hasImageHint = !!card.querySelector('img');

          if (!hasPriceHint && !hasImageHint) continue;

          const imgEl = card.querySelector('img') || a.querySelector('img') || null;

          let img = '';

          if (imgEl) {
            img =
              imgEl.getAttribute('src') ||
              imgEl.getAttribute('data-src') ||
              pickBestSrcFromSrcset(imgEl.getAttribute('srcset')) ||
              pickBestSrcFromSrcset(imgEl.getAttribute('data-srcset')) ||
              '';

            img = normalizeImgUrl(img);
          }

          let title =
            /* Breed results (owner, 2026-10-29): the redesigned grid card's name; its first link is
               the picture's, with only its badge ("Best Seller") for words */
            (card.querySelector('.pz-product-card__title')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (card.querySelector('.card__heading a')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (card.querySelector('.card__heading')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (card.querySelector('.card__information .full-unstyled-link')?.textContent || '')
              .replace(/\s+/g, ' ')
              .trim() ||
            (card.querySelector('.full-unstyled-link')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (a.textContent || '').replace(/\s+/g, ' ').trim();

          if (!title || title.toLowerCase() === 'new') {
            title = 'View product';
          }

          const priceEl =
            card.querySelector('.price-item--regular') ||
            card.querySelector('.price-item') ||
            card.querySelector('.price') ||
            card.querySelector('.money') ||
            card.querySelector('[class*="price"]');

          /* Breed results (owner, 2026-10-29): the words the card shows, not its hidden flags */
          const price = cleanPriceText(visibleText(priceEl));

          seen.add(url);

          products.push({
            title,
            url,
            img,
            price,
          });

          if (products.length >= limit) break;
        }

        collectionProductsCache.set(cacheKey, products);

        return products;
      })();

      collectionProductsInflight.set(cacheKey, request);

      try {
        return await request;
      } finally {
        collectionProductsInflight.delete(cacheKey);
      }
    }

    async function fetchProductJsonByHandle(handle) {
      if (!handle) return null;

      if (productJsonCache.has(handle)) {
        return productJsonCache.get(handle);
      }

      if (productJsonInflight.has(handle)) {
        return productJsonInflight.get(handle);
      }

      const p = (async () => {
        const url = `${location.origin}/products/${handle}.js`;
        const res = await fetch(url, { credentials: 'same-origin' });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();

        productJsonCache.set(handle, json);

        return json;
      })();

      productJsonInflight.set(handle, p);

      try {
        return await p;
      } finally {
        productJsonInflight.delete(handle);
      }
    }

    function pickVariantForSize(productJson, desiredSizeValue) {
      if (!productJson || !Array.isArray(productJson.variants) || !desiredSizeValue) {
        return null;
      }

      const optionNames = Array.isArray(productJson.options) ? productJson.options : [];
      let sizeIndex = -1;

      const hints =
        Array.isArray(variantOptionNameHints) && variantOptionNameHints.length
          ? variantOptionNameHints
          : ['size'];

      for (let i = 0; i < optionNames.length; i++) {
        const name = String(optionNames[i] || '').toLowerCase();

        if (hints.some((h) => name.includes(String(h).toLowerCase()))) {
          sizeIndex = i;
          break;
        }
      }

      if (sizeIndex < 0) sizeIndex = 0;

      const desired = String(desiredSizeValue).trim().toLowerCase();

      const exact = productJson.variants.find((v) => {
        const opts = Array.isArray(v.options) ? v.options : [];
        const val = String(opts[sizeIndex] || '').trim().toLowerCase();

        return val === desired;
      });

      if (exact) return exact;

      const any = productJson.variants.find((v) => {
        const opts = Array.isArray(v.options) ? v.options : [];

        return opts.some((x) => String(x || '').trim().toLowerCase() === desired);
      });

      return any || null;
    }

    async function enrichProductsWithSizeVariant(products, sizeKey) {
      const desiredSizeValue = getSizeOptionValue(sizeKey);

      if (!desiredSizeValue) return products || [];

      const list = Array.isArray(products) ? products.slice(0, maxVariantFetch) : [];
      const rest = Array.isArray(products) ? products.slice(maxVariantFetch) : [];

      const out = await Promise.all(
        list.map(async (p) => {
          const handle = getHandleFromUrl(p.url);

          if (!handle) {
            return { ...p };
          }

          try {
            const json = await fetchProductJsonByHandle(handle);
            const variant = pickVariantForSize(json, desiredSizeValue);

            if (variant) {
              const currency =
                (window.Shopify && Shopify.currency && Shopify.currency.active) ||
                (window.Shopify && Shopify.currency && Shopify.currency.default) ||
                '';

              const sizePriceText =
                typeof window.Shopify?.formatMoney === 'function'
                  ? Shopify.formatMoney(variant.price, window.Shopify?.money_format || undefined)
                  : formatMoneyFromCents(variant.price, currency);

              return {
                ...p,
                title: json?.title || p.title || 'View product',
                variantId: variant.id,
                price: p.price || cleanPriceText(sizePriceText) || sizePriceText,
                url: `${p.url.split('?')[0]}?variant=${variant.id}`,
                sizeValue: desiredSizeValue,
              };
            }

            return {
              ...p,
              title: json?.title || p.title || 'View product',
            };
          } catch (e) {
            return { ...p };
          }
        })
      );

      return [...out, ...rest];
    }

    async function getProductsForBreed(breed) {
      const bedSizeKey = detectSizeForBreed(breed);
      const treeSizeKey = bedSizeKey;
      const scratcherSizeKey = bedSizeKey;

      const sources = [];

      const bedUrl = bedSizeUrls?.[bedSizeKey];

      if (bedUrl) {
        sources.push({
          key: 'beds',
          label: 'CAT BEDS & CAVES',
          sizeKey: bedSizeKey,
          url: bedUrl,
        });
      }

      const treeUrl = treeSizeUrls?.[treeSizeKey];

      if (treeUrl) {
        sources.push({
          key: 'trees',
          label: 'CAT TREES & TOWERS',
          sizeKey: treeSizeKey,
          url: treeUrl,
        });
      }

      const scratcherUrl = scratcherSizeUrls?.[scratcherSizeKey];

      if (scratcherUrl) {
        sources.push({
          key: 'scratchers',
          label: 'CAT SCRATCHERS',
          sizeKey: scratcherSizeKey,
          url: scratcherUrl,
        });
      }

      return {
        bedSizeKey,
        treeSizeKey,
        scratcherSizeKey,
        sources,
      };
    }

    return {
      normSearch,
      uniqueSorted,

      alreadyShown,
      markShown,
      getSelectedBreed,
      setSelectedBreed,
      removeSelectedBreed,

      loadBreeds,
      detectSizeForBreed,
      getProductsForBreed,
      getSizeOptionValue,

      fetchCollectionSectionHTML,
      fetchCollectionProductsFallback,
      enrichProductsWithSizeVariant,
    };
  }

  window.PURRZ_BREED_MODAL_CORE = { createCore };
})();
